/**
 * Connector Performance — Analytics, third tab
 *
 * OPERATIONS, not money. The other two tabs count fulfilled orders, commission and
 * payout. This counts what retailers sent us and what became of it, which is a different
 * and always-larger number — orders rejected, cancelled or still in flight never become
 * billable. The tab therefore OPENS by reconciling the two counts, rather than leaving an
 * admin to notice that "Orders" says one thing here and another next door. Two unexplained
 * order counts on one screen is how a dashboard loses trust.
 *
 * Nothing here is live. Orders arrive by polling — the retailer API has no push channel —
 * so every figure is as of the last sweep, and any lag shown is our own rather than the
 * retailer's. The header says so explicitly.
 *
 * Figures with no evidence behind them render as "—" with the reason on hover, never as
 * zero. A zero acceptance rate and an unmeasured one look identical otherwise, and they
 * mean opposite things.
 */

// No hooks left here: the only state this component held was the retailer table's paging, and
// that table is gone. What remains renders straight from the query result.
import {
  Card, Text, Box, BlockStack, InlineStack, InlineGrid, Badge, Banner,
  IndexTable, Spinner, Tooltip, Divider, ProgressBar,
} from '@shopify/polaris';
import { useConnectorPerformance } from '../../utils/queries';
import { emptyRow, headingsFor } from '../../utils/table';

const when = (d) => (d ? new Date(d).toLocaleString() : 'never');

/**
 * Column definitions for this card's two tables. See utils/table.jsx for why an empty table
 * needs a synthetic row to keep its headings.
 */
const LOSS_COLUMNS = [
  { key: 'reason', title: 'Reason', tip: 'Why the order did not become a Shopify order. These are the states we actually record — a state nothing writes cannot appear here.' },
  { key: 'count', title: 'Count', tip: 'Orders in this period at that reason.' },
  { key: 'means', title: 'What it means', tip: 'Whether the sale is recoverable and whose it is to recover. A lost sale cannot be got back; a recoverable one still can.' },
];

export default function ConnectorPerformance({ days = 30, brandId = null }) {
  const { data, isLoading, error } = useConnectorPerformance({
    days,
    ...(brandId ? { brandId } : {}),
  });

  /**
   * Paging state for the retailer table only — declared before the early returns below, since
   * a hook cannot sit after one.
   *
   * "Where orders are lost" gets NO row count: its rows are the fixed set of loss reasons we
   * record, and the point of that table is to read them against each other. Paging a
   * five-row enumeration would hide half a comparison to save nothing.
   */
  if (error) {
    return <Banner tone="critical">Unable to load connector performance: {error.message}</Banner>;
  }
  if (isLoading || !data) {
    return <Box padding="600"><Spinner /></Box>;
  }

  // `health` is no longer destructured — the table that used it is gone. The endpoint still
  // returns it; trimming that is a server change worth making separately.
  const { reconciliation: rec, stats, funnel, losses } = data;

  const stat = (label, value, sub, help, tone) => (
    <Card>
      <Box padding="400">
        <BlockStack gap="100">
          <InlineStack gap="100" blockAlign="center">
            <Text variant="bodySm" tone="subdued">{label}</Text>
            {help && <Tooltip content={help}><Text variant="bodySm" tone="subdued">ⓘ</Text></Tooltip>}
          </InlineStack>
          <Text variant="heading2xl" tone={tone}>{value}</Text>
          {sub && <Text variant="bodySm" tone="subdued">{sub}</Text>}
        </BlockStack>
      </Box>
    </Card>
  );

  return (
    <BlockStack gap="500">

      {/* The reconciliation comes first, deliberately. */}
      <Banner tone="info" title="These are operational counts, not billing figures">
        <BlockStack gap="100">
          <Text variant="bodyMd">
            &ldquo;Received from retailer&rdquo; below is <strong>{rec.received}</strong>, while the billing
            tabs show <strong>{rec.billable}</strong>.
            {rec.difference !== 0 && (
              <> The difference is {Math.abs(rec.difference)} order
                {Math.abs(rec.difference) === 1 ? '' : 's'} that {rec.difference > 0
                  ? 'were rejected, cancelled or are still in flight — they never became billable'
                  : 'are billable but did not come through the connector'}. Both numbers are correct.</>
            )}
          </Text>
          <Text variant="bodySm" tone="subdued">{data.notLive}</Text>
        </BlockStack>
      </Banner>

      {/* Three cards, not four. Acceptance Rate was removed on the client's call of
          Aug 5 — his reasoning was that we cannot track it dependably, and a rate that
          is sometimes wrong is worse than no rate when it is the number you would use
          to judge whether the connector is healthy. Lost to Stock-outs survives and
          carries the part that mattered: it counts actual sales we could not take,
          which is a fact rather than a ratio.

          Mirakl's own grading of us (A21) is not shown anywhere in the app: it was
          dropped on 2026-08-12. It supports only five fixed periods, so it could never
          follow a date range the admin picks, and we have no test account to confirm
          which of its fields a retailer actually returns. */}
      <InlineGrid columns={{ xs: 1, sm: 3 }} gap="400">
        {stat('Orders Received', stats.ordersReceived, 'before acceptance',
          'Orders retailers sent us in this period, before acceptance. Not a billing figure.')}
        {stat(
          'Avg Tracking Lag',
          stats.avgTrackingLag ?? '—',
          stats.avgTrackingLag ? `over ${stats.avgTrackingLagSample} orders` : 'not yet measurable',
          stats.avgTrackingLagReason
            || 'Average time between a brand fulfilling and the retailer receiving tracking. This is our lag, not theirs.',
        )}
        {stat('Lost to Stock-outs', stats.lostToStockOuts,
          stats.lostToStockOuts > 0 ? 'sales we could not take' : 'none',
          'Orders rejected because stock was unavailable or unreadable, so they never became billable. Each is a sale we could not take.',
          stats.lostToStockOuts > 0 ? 'critical' : undefined)}
      </InlineGrid>

      {/* These two sit side by side, as the design has them: the funnel shows where
          orders drop off and the table beside it says why. Stacked full-width they
          read as unrelated sections and the reader has to scroll between a count and
          its explanation. Single column below md, where two would be unreadable. */}
      <InlineGrid columns={{ xs: 1, md: 2 }} gap="400">

      {/* ---- Order flow ------------------------------------------------------ */}
      <Card>
        <Box padding="400">
          <BlockStack gap="300">
            <BlockStack gap="050">
              <Text variant="headingMd" as="h3">Order flow</Text>
              <Text variant="bodySm" tone="subdued">
                Where orders drop off between the retailer and tracking confirmation.
              </Text>
            </BlockStack>
            <BlockStack gap="300">
              {funnel.map((s, i) => (
                <BlockStack gap="100" key={s.step}>
                  <InlineStack align="space-between" blockAlign="center">
                    <InlineStack gap="150" blockAlign="center">
                      <Text variant="bodySm" tone="subdued">{i + 1}.</Text>
                      <Text variant="bodyMd">{s.step}</Text>
                      {s.unknown > 0 && (
                        <Tooltip content={s.reason || `${s.unknown} order(s) have no recorded decision for this step, so they are counted as not having reached it.`}>
                          <Badge tone="attention">{`${s.unknown} unknown`}</Badge>
                        </Tooltip>
                      )}
                    </InlineStack>
                    <Text variant="bodyMd" fontWeight="semibold">
                      {/* null means unmeasured, not zero — a step with no evidence shows a
                          dash rather than a number that would contradict the steps below. */}
                      {s.count == null ? (
                        <Tooltip content={s.reason || 'Not measurable for this period'}>
                          <Text as="span" tone="subdued">—</Text>
                        </Tooltip>
                      ) : s.count}
                      <Text as="span" variant="bodySm" tone="subdued">
                        {s.pct == null ? '' : ` · ${s.pct}%`}
                      </Text>
                    </Text>
                  </InlineStack>
                  {s.count == null ? (
                    <Box paddingBlockStart="100" paddingBlockEnd="100">
                      <Text variant="bodySm" tone="subdued">{s.reason}</Text>
                    </Box>
                  ) : (
                    <ProgressBar progress={s.pct ?? 0} size="small" tone={i === 0 ? 'primary' : 'highlight'} />
                  )}
                </BlockStack>
              ))}
            </BlockStack>
          </BlockStack>
        </Box>
      </Card>

      {/* ---- Where orders are lost ------------------------------------------- */}
      <Card>
        <Box padding="400" paddingBlockEnd="200">
          <BlockStack gap="050">
            <Text variant="headingMd" as="h3">Where orders are lost</Text>
            <Text variant="bodySm" tone="subdued">
              A lost sale cannot be recovered; a recoverable one is still ours if we act.
            </Text>
          </BlockStack>
        </Box>
        <IndexTable
          resourceName={{ singular: 'reason', plural: 'reasons' }}
          itemCount={losses.length || 1}
          selectable={false}
          headings={headingsFor(LOSS_COLUMNS)}
        >
          {losses.length === 0 ? emptyRow(
            LOSS_COLUMNS, 'losses',
            'No order was lost in this period — every order fetched from a retailer became a Shopify order.',
          ) : losses.map((l, i) => (
            <IndexTable.Row id={l.reason} key={l.reason} position={i}>
              <IndexTable.Cell>
                <InlineStack gap="150" blockAlign="center">
                  <Text variant="bodySm" fontWeight="semibold">{l.reason}</Text>
                  {l.count > 0 && (
                    <Badge tone={l.lost ? 'critical' : 'attention'}>
                      {l.lost ? 'Lost sale' : 'Recoverable'}
                    </Badge>
                  )}
                </InlineStack>
              </IndexTable.Cell>
              <IndexTable.Cell>
                <Text variant="bodyMd" fontWeight="semibold" tone={l.count > 0 && l.lost ? 'critical' : undefined}>
                  {l.count}
                </Text>
              </IndexTable.Cell>
              <IndexTable.Cell><Text variant="bodySm" tone="subdued">{l.means}</Text></IndexTable.Cell>
            </IndexTable.Row>
          ))}
        </IndexTable>
      </Card>

      </InlineGrid>

      {/* A "Sync health by retailer" table stood here — orders received, SKUs listed, last
          inventory sync, tracking lag, on-time rate and a status badge, one row per retailer.
          Removed as duplication rather than as clutter: the Brands table already reports SKUs,
          Orders and Tracking last synced per connection, and Background Jobs -> Sync History is
          the permanent record of every run with its outcome. Three places answering "is syncing
          healthy" is two places to disagree.

          What stays here is what nothing else answers: where orders drop off, and why they were
          lost. */}

      <Divider />
      <Box paddingBlockEnd="400">
        {/* This previously directed the reader to a Retailer Scorecard on Retailer
            Accounts. That screen was dropped, so the pointer would have been a lie —
            the remaining half of the sentence is the part that still matters: these
            are our own figures, on our own polling cadence. */}
        <Text variant="bodySm" tone="subdued">
          Every figure here is ours — measured from our own order decisions and our own
          polling cadence, not from the retailer&rsquo;s grading of us. As of {when(data.asOf)}.
        </Text>
      </Box>
    </BlockStack>
  );
}
